import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useCustomer } from "@/contexts/CustomerContext";
import ArrowRightIcon from "@/components/arrow/ArrowRightIcon";
import { useMerchantSlug } from "@/utils/hooks/useMerchantSlug";
import {
  useGenerateOtpMutation,
  useVerifyOtpMutation,
} from "@/store/api/userApi";
import Button from "../ui/Button";
import CloseIcon from "@/assets/img/CloseIcon";

interface CredentialRequestOptionsWithOTP extends CredentialRequestOptions {
  otp?: {
    transport: Array<"sms">;
  };
}

const CustomerLogin = () => {
  const { login, customer } = useCustomer();
  const [verificationScreen, setVerificationScreen] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [fullPhoneNumber, setFullPhoneNumber] = useState("");
  const merchantSlug = useMerchantSlug();
  const navigate = useNavigate();

  // store OTP digits in an array, but also combine them into `codeString`.
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (customer) {
      navigate(`${merchantSlug}/cart`);
    }
  }, [customer, merchantSlug, navigate]);

  useEffect(() => {
    const phoneFromParams = searchParams.get("phone");
    if (phoneFromParams) {
      setPhoneValue(phoneFromParams);
      setFullPhoneNumber("+1" + phoneFromParams);
    }
  }, [searchParams]);

  useEffect(() => {
    if (verificationScreen) {
      const timer = setInterval(() => {
        setCountdown((prevCount) => {
          if (prevCount <= 1) {
            clearInterval(timer);
            setIsButtonEnabled(true);
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [verificationScreen]);

  // --- Android: Auto-fill OTP using the Web OTP API ---
  useEffect(() => {
    if (verificationScreen && "OTPCredential" in window) {
      const ac = new AbortController();
      navigator.credentials
        .get({
          otp: { transport: ["sms"] },
          signal: ac.signal,
        } as CredentialRequestOptionsWithOTP)
        .then((otpCredential: any) => {
          if (otpCredential && otpCredential.code) {
            const codeDigits = otpCredential.code.split("");
            if (codeDigits.length === 4) {
              setOtp(codeDigits);
              handleOTPVerify();
            }
          }
        })
        .catch((err: any) => {
          console.error("Error retrieving OTP via WebOTP API:", err);
        });
      return () => ac.abort();
    }
  }, [verificationScreen]);

  // OTP autofill handler
  const handleOTPAutofill = (text: string) => {
    const code = text;
    const focusInput = (index: number) => {
      inputRefs.current[index]?.focus();
    };
    if (code && code.length === 4) {
      setOtp(code.split(""));
      focusInput(3);
    }
  };

  const [generateOTP] = useGenerateOtpMutation();
  const [verifyOTP] = useVerifyOtpMutation();

  const handleChange = (value: string, index: number) => {
    const digit = value.replace(/[^\d]/, "");
    if (!digit) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = digit;
    setOtp(updatedOtp);

    // Automatically move focus to next input
    if (index < 3 && digit) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Backspace") {
      event.preventDefault(); // Stop the default "delete the entire input"
      const updatedOtp = [...otp];

      if (otp[index]) {
        // If there's a digit in the current box - clear it
        updatedOtp[index] = "";
      } else if (index > 0) {
        //else - go back one input
        updatedOtp[index - 1] = "";
        inputRefs.current[index - 1]?.focus();
      }
      setOtp(updatedOtp);
    }
  };

  // Format phone number as (xxx) xxx-xxxx
  const formatMobileNumber = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    let formattedValue = cleanedValue;

    if (cleanedValue.length > 3) {
      formattedValue = `(${cleanedValue.slice(0, 3)}) ${cleanedValue.slice(3)}`;
    }
    if (cleanedValue.length > 6) {
      formattedValue = `(${cleanedValue.slice(0, 3)}) ${cleanedValue.slice(
        3,
        6,
      )}-${cleanedValue.slice(6, 10)}`;
    }

    return [cleanedValue, formattedValue];
  };

  // Handle phone input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const [cleanedValue, formattedValue] = formatMobileNumber(value);

    setPhoneValue(cleanedValue);
    setFullPhoneNumber("+1" + cleanedValue);

    // Validate phone
    let errMsg = "";
    if (cleanedValue.length !== 10) {
      errMsg = "Phone number must be 10 digits";
    }
    setError(errMsg);

    // Update the visible text
    e.target.value = formattedValue;
  };

  // Generate OTP (form submission)
  const handleOTPGenerate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!phoneValue || phoneValue.length !== 10) {
      setError("Phone number must be 10 digits");
      return;
    }
    try {
      await generateOTP({ phone: `+1${phoneValue}` }).unwrap();
      setVerificationScreen(true);
      setCountdown(30);
      setIsButtonEnabled(false);
    } catch (err) {
      toast.error("Failed to generate OTP. Please try again.");
    }
  };

  // Verify OTP (form submission)
  const handleOTPVerify = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const ac = new AbortController();
    ac.abort(); // Stop webotp api

    // Combine digits from the array
    const codeString = otp.join("");
    if (codeString.length !== 4) {
      toast.error("Please enter the 4-digit code.");
      return;
    }

    try {
      const res = await verifyOTP({
        phone: fullPhoneNumber,
        otp: codeString,
      }).unwrap();
      if (res && res.user) {
        const user = {
          name: res.user.name,
          phone: res.user.phone,
          email: res.user.email,
          avatar: res.user.avatar,
          street: res.user.street,
          city: res.user.city,
          province: res.user.state,
          postal_code: res.user.pincode,
          country: res.user.country,
          wallet_points: "0.00",
        };
        login(user);
        toast.success("Logged in successfully");
        navigate(`${merchantSlug}/cart`);
      } else {
        // If the user doesn't exist, go to register
        navigate(`${merchantSlug}/customer-register?phone=${phoneValue}`);
      }
    } catch (err) {
      toast.error("Invalid code or verification failed.");
    }
  };

  const handleResendOTP = async () => {
    try {
      await generateOTP({ phone: `+1${phoneValue}` }).unwrap();
      setCountdown(30);
      setIsButtonEnabled(false);
    } catch (err) {
      toast.error("Failed to resend OTP. Please try again.");
    }
  };

  return (
    <div className="overflow-hidden h-screen">
      {/* Top-right close button */}
      <div className="flex justify-start p-4">
        <button onClick={() => navigate(-1)} className="hover:underline">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      {/* Verification Screen */}
      {verificationScreen ? (
        <div className="flex min-h-screen flex-1 flex-col px-6 py-12 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm px-2">
            <p className="mt-10 text-base sm:text-2xl font-bold leading-9 tracking-tight text-gray-900">
              Enter the 4 digit code sent to you at{"  "}
              <span className="block sm:inline">
                {formatMobileNumber(phoneValue)[1]}
              </span>
            </p>
          </div>

          <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
            <form onSubmit={handleOTPVerify} className="space-y-6">
              <div className="flex space-x-2 justify-center">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="tel"
                    data-type="otp"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    value={digit}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length === 4) {
                        handleOTPAutofill(value);
                        return;
                      }
                      if (value && value.length === 1) {
                        handleChange(value, index);
                      }
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className={`w-12 h-12 font-bold text-center border-0 text-lg bg-grayCloud focus:outline-none focus:ring-0 focus:border-2 focus:border-black`}
                    style={{ caretColor: "transparent" }}
                  />
                ))}
              </div>
              <div className="w-full flex flex-col gap-4 items-center justify-center">
                <Button
                  variant="secondary"
                  text={`I haven't received a code ${
                    countdown > 0 ? `(${countdown.toFixed(0)})` : ""
                  }`}
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] 
                    text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                  onClick={handleResendOTP}
                  disabled={!isButtonEnabled}
                />

                <Button
                  variant="primary"
                  text="Next"
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] 
                      text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                  onClick={handleOTPVerify}
                />
              </div>
            </form>
          </div>
        </div>
      ) : (
        // Phone Input Screen
        <div className="flex min-h-screen flex-1 flex-col mt-12 px-6 py-12 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <span className="mt-10 text-center text-base sm:text-2xl font-bold leading-9 tracking-tight text-gray-900">
              Enter your mobile number
            </span>
          </div>

          <div className="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
            <form onSubmit={handleOTPGenerate} className="space-y-6">
              <div>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm border-gray-100 rounded-l-lg bg-gray-100 px-3 h-full flex items-center justify-center">
                      +1
                    </span>
                  </div>
                  <input
                    type="tel"
                    name="phone_number"
                    id="phone_number"
                    className="w-full border-gray-100 border-2 rounded-lg pl-16 sm:text-sm focus:outline-none focus:ring-0 focus:border-gray-100"
                    placeholder="(xxx) xxx-xxxx"
                    onChange={handleInputChange}
                    maxLength={14}
                    required
                  />
                </div>
                {error && (
                  <div className="text-redCrimson font-normal m-2 text-sm">
                    {error}
                  </div>
                )}
              </div>

              <div>
                <p className="px-2 font-bold text-xs text-ironGray">
                  By proceeding, you consent to get SMS messages, including by
                  automated means, from Redbird and its affiliates to the number
                  provided.
                </p>
              </div>

              <div className="w-full flex justify-center">
                <Button
                  variant="primary"
                  text="Next"
                  className="w-full h-[3rem] md:h-[3.4rem] rounded-[6px] md:rounded-[8px] 
                              text-[16px] md:text-[18px] font-bold flex items-center justify-center"
                  onClick={handleOTPGenerate}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerLogin;
